import React from 'react';
import ReactDOM from 'react-dom/client';
import { BrowserRouter, Routes, Route } from 'react-router-dom';

// Import Bootstrap CSS and JS locally (avoids CDN blocking by privacy browsers)
import 'bootstrap/dist/css/bootstrap.min.css';
import 'bootstrap-icons/font/bootstrap-icons.css';
import 'bootstrap/dist/js/bootstrap.bundle.min.js';

import './index.css';
import './dashboard.css';
import App from './App';
import { ThemeProvider } from './ThemeContext';

// Debug logging for deployment issues
console.log('🚀 App starting...', {
  url: window.location.href,
  pathname: window.location.pathname,
  search: window.location.search,
  userAgent: navigator.userAgent
});

// ============================================================
// PWA AUTO-UPDATE HANDLER
// ============================================================
const setupPWAUpdates = () => {
  if (!('serviceWorker' in navigator)) return;
  
  // Listen for service worker updates
  navigator.serviceWorker.addEventListener('controllerchange', () => {
    console.log('🔄 New service worker activated, reloading for updates...');
    // Only reload if the page is visible and not currently reloading
    if (!document.hidden && !window.isReloading) {
      window.isReloading = true;
      window.location.reload();
    }
  });
  
  // Listen for messages from service worker
  navigator.serviceWorker.addEventListener('message', (event) => {
    if (event.data && event.data.type === 'SW_UPDATED') {
      console.log('📦 Service worker updated to version:', event.data.version);
      // Page will auto-reload via controllerchange event
    }
  });
  
  // Register service worker for PWA auto-update
  navigator.serviceWorker.register('/firebase-messaging-sw.js', {
    updateViaCache: 'none'
  }).then((registration) => {
    console.log('✅ PWA Service Worker registered');
    
    // Check for updates on page focus (when user returns to app)
    document.addEventListener('visibilitychange', () => {
      if (!document.hidden) {
        registration.update().catch(() => {});
      }
    });
    
    // Check for updates every 5 minutes
    setInterval(() => {
      registration.update().catch(() => {});
    }, 5 * 60 * 1000);
    
  }).catch((error) => {
    // Silently handle SW registration failures in development
    if (window.location.hostname !== 'localhost') {
      console.warn('Service Worker registration failed:', error);
    }
  });
};

// Initialize PWA updates
setupPWAUpdates();

// Hide the loading fallback once React starts
const hideLoader = () => {
  const loader = document.getElementById('app-loading');
  if (loader) {
    loader.style.display = 'none';
  }
};

// Error boundary for the entire app
class AppErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error) {
    return { hasError: true, error };
  }

  componentDidCatch(error, errorInfo) {
    console.error('❌ App crashed:', error, errorInfo);
  }

  render() {
    if (this.state.hasError) {
      return (
        <div style={{
          minHeight: '100vh',
          background: 'linear-gradient(135deg, #0f0f1a 0%, #1a1a2e 50%, #16213e 100%)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          padding: '20px',
          boxSizing: 'border-box'
        }}>
          <div style={{
            background: 'white',
            borderRadius: '20px',
            padding: '30px',
            maxWidth: '400px',
            width: '100%',
            textAlign: 'center'
          }}>
            <div style={{ fontSize: '3rem', marginBottom: '16px' }}>⚠️</div>
            <h2 style={{ color: '#1e293b', marginBottom: '12px' }}>Something went wrong</h2>
            <p style={{ color: '#64748b', marginBottom: '20px', fontSize: '0.9rem' }}>
              {this.state.error?.message || 'An unexpected error occurred'}
            </p>
            <button
              onClick={() => window.location.reload()}
              style={{
                background: '#3b82f6',
                color: 'white',
                border: 'none',
                borderRadius: '10px',
                padding: '14px 28px',
                fontSize: '1rem',
                cursor: 'pointer',
                width: '100%'
              }}
            >
              🔄 Refresh Page
            </button>
          </div>
        </div>
      );
    }
    return this.props.children;
  }
}

const root = ReactDOM.createRoot(document.getElementById('root'));

// Wrap everything in try-catch
try {
  root.render(
    <React.StrictMode>
      <AppErrorBoundary>
        <ThemeProvider>
          <BrowserRouter>
            <Routes>
              <Route path="/*" element={<App />} />
            </Routes>
          </BrowserRouter>
        </ThemeProvider>
      </AppErrorBoundary>
    </React.StrictMode>
  );
  hideLoader();
  console.log('✅ App rendered successfully');
} catch (error) {
  console.error('❌ Failed to render app:', error);
  hideLoader();
  document.getElementById('root').innerHTML = `
    <div style="min-height: 100vh; display: flex; align-items: center; justify-content: center; background: #1a1a2e; padding: 20px;">
      <div style="background: white; padding: 30px; border-radius: 20px; text-align: center; max-width: 400px;">
        <div style="font-size: 3rem; margin-bottom: 16px;">⚠️</div>
        <h2 style="color: #1e293b;">Failed to Load</h2>
        <p style="color: #64748b;">${error.message}</p>
        <button onclick="location.reload()" style="margin-top: 20px; padding: 12px 24px; background: #3b82f6; color: white; border: none; border-radius: 8px; cursor: pointer;">
          🔄 Refresh
        </button>
      </div>
    </div>
  `;
}
